import React, { useId } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import './Modal.css';

/**
 * Reusable ConfirmDialog component for confirmation actions
 * @param {boolean} isOpen - Whether the dialog is visible
 * @param {function} onClose - Callback when dialog is closed/cancelled
 * @param {function} onConfirm - Callback when user confirms
 * @param {string} title - Dialog title
 * @param {string} message - Dialog message content
 * @param {string} confirmText - Text for confirm button (default: 'Confirm')
 * @param {string} cancelText - Text for cancel button (default: 'Cancel')
 * @param {string} variant - Button variant: 'danger', 'primary' (default: 'danger')
 */
const ConfirmDialog = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'danger'
}) => {
  const titleId = useId();
  const messageId = useId();

  if (!isOpen) return null;

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  const isDanger = variant === 'danger';

  return (
    <div className="ui-dialog-overlay ui-dialog-overlay--confirm" onClick={onClose}>
      <div
        className="ui-dialog ui-dialog--confirm"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-describedby={message ? messageId : undefined}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ui-dialog-head">
          <div className="ui-dialog-heading">
            {isDanger && (
              <span className="ui-dialog-icon ui-dialog-icon--danger" aria-hidden="true">
                <AlertTriangle size={18} />
              </span>
            )}
            <h2 id={titleId} className="ui-dialog-title">
              {title}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="ui-dialog-close"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div
          id={messageId}
          className={`ui-dialog-body ui-dialog-message${isDanger ? ' ui-dialog-body--indented' : ''}`}
        >
          {message}
        </div>

        <div className="ui-dialog-foot">
          <button
            type="button"
            onClick={onClose}
            className="btn btn-secondary"
          >
            {cancelText}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            className={isDanger ? 'btn btn-danger' : 'btn btn-primary'}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;
